import { ReactNode, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { TamaguiProvider, isWeb } from 'tamagui'
import {
  ThemeProvider as NavigationThemeProvider,
  DefaultTheme,
} from '@react-navigation/native'
import AsyncStorage from '@react-native-async-storage/async-storage'

import config from '../theme'
import { registerSchoolTheme } from '../theme/registerSchoolTheme'
import { useHasFeature } from '@hooks/useThemeParams'
import { useThemeColor } from '@hooks/useThemeColor'
import { THEME_DARK_MODE, THEME_LIGHT_MODE } from '@constants/features'
import { NATIVE_BASE_URL } from '@constants/storages'
import {
  activeSchoolSeed,
  hydrateSchoolThemeStore,
  useSchoolThemeStore,
} from '@stores/useSchoolThemeStore'

type ThemeName = keyof typeof config.themes

function NavigationTheme({ children }: { children: ReactNode }) {
  const background = useThemeColor('bg-page')
  const navigationTheme = useMemo(
    () => ({
      ...DefaultTheme,
      colors: { ...DefaultTheme.colors, background },
    }),
    [background],
  )

  return (
    <NavigationThemeProvider value={navigationTheme}>
      {children}
    </NavigationThemeProvider>
  )
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const isLightMode = useHasFeature(THEME_LIGHT_MODE)
  const isDarkMode = useHasFeature(THEME_DARK_MODE)
  const schoolSeed = useSchoolThemeStore((state) => activeSchoolSeed(state))

  useEffect(() => {
    if (isWeb) return
    AsyncStorage.getItem(NATIVE_BASE_URL)
      .then((url) => {
        if (url) return hydrateSchoolThemeStore(new URL(url).hostname)
      })
      .catch(() => undefined)
  }, [])

  const [schoolTheme, setSchoolTheme] = useState<string | null>(null)

  // addTheme notifica na hora todo componente com tema montado; no render isso
  // vira setState em outro componente. O layout effect registra antes da
  // pintura, então a tela não chega a aparecer com o tema default.
  useLayoutEffect(() => {
    setSchoolTheme(schoolSeed ? registerSchoolTheme(schoolSeed) : null)
  }, [schoolSeed])

  const theme = useMemo(() => {
    if (schoolTheme) return schoolTheme
    if (isLightMode) return 'light'
    if (isDarkMode) return 'dark'
    return 'dark'
  }, [isDarkMode, isLightMode, schoolTheme])

  return (
    <TamaguiProvider config={config} defaultTheme={theme as ThemeName}>
      <NavigationTheme>{children}</NavigationTheme>
    </TamaguiProvider>
  )
}
